import { useState, useEffect } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";
import {
	getInitialButtonPosition,
	getInitialButtonPositionMobile,
	getInitialColor,
} from "@/lib/utils";
import { DEFAULT_FEATURES } from "@/constant/settings";
import { isPremiumFeature } from "@/constant/premium-features";

// Frontend enrichFeatures function for widget use
function enrichFeaturesForWidget(backendFeatures = []) {
	// If we have backend features, use them as-is (they're already processed)
	if (backendFeatures && backendFeatures.length > 0) {
		return backendFeatures;
	}

	// Fallback: use DEFAULT_FEATURES with proper defaults
	return DEFAULT_FEATURES.map((category) => ({
		...category,
		settings: category.settings.map((setting) => {
			const isPremium = isPremiumFeature(setting.key);
			// Default enabled state: premium features off, others on
			const defaultEnabled = !isPremium;

			return {
				...setting,
				enabled: setting.enabled ?? defaultEnabled,
				premium: isPremium,
			};
		}),
	}));
}

export default function useWidget() {
	const [settings, setSettings] = useState(() => {
		// Attempt to hydrate from localStorage svgSettings to avoid flicker
		let persisted = null;
		if (typeof window !== "undefined") {
			try {
				persisted = JSON.parse(localStorage.getItem("svgSettings"));
			} catch {
				/* ignore */
			}
		}
		return {
			svgSettings: {
				// Align defaults with admin use-settings hook
				customSvg: null,
				processedSvg: null,
				size: "sm",
				radius: "sm",
				backgroundColor: getInitialColor(),
				buttonLabel: "Accessibility",
				labelButtonActive: false,
				showAccessibilityProfile: true,
				bgColor: {
					type: "solid",
					color: getInitialColor(),
				},
				selectedIcon: "Accessibility",
				buttonPosition:
					typeof window !== "undefined"
						? getInitialButtonPosition()
						: "bottom-right",
				buttonPositionMobile:
					typeof window !== "undefined"
						? getInitialButtonPositionMobile()
						: "bottom-right",
				exactPosition: {
					enabled: false,
					horizontalOffset: 0,
					verticalOffset: 0,
					horizontalDirection: "left",
					verticalDirection: "lower",
				},
				enableWidget: true,
				draggableEnabled: false,
				displayMode: "all",
				displayContainerMode: "popover",
				containerTheme: "default",
				includePostTypes: [],
				excludePostTypes: [],
				openKeyboardShortcuts: false,
				hideLogo: false,
				keyboardShortcut: "Alt+Shift+A",
				hideOnDevices: { desktop: false, tablet: false, mobile: false },
				clickSpark: {
					color: "#155eef",
					size: 10,
					radius: 60,
					count: 8,
					duration: 500,
					scale: 1,
					easing: "ease-out",
				},
				imageTrails: {
					images: [],
					variant: 1,
				},
				...(persisted || {}), // overlay persisted (safe keys)
			},
			features: enrichFeaturesForWidget([]),
			statementSettings: {},
		};
	});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function fetchSettings() {
			try {
				const data = await apiFetch({ path: "/zn7/v1/widget-settings" });

				// Get statement settings from the public endpoint
				let statementSettings = {};
				if (data?.statementSettings && data.statementSettings.widget_enabled) {
					statementSettings = data.statementSettings;
				}

				setSettings((prevSettings) => ({
					svgSettings: {
						...prevSettings.svgSettings,
						...(data?.svgSettings || {}),
						displayContainerMode:
							data?.svgSettings?.displayContainerMode ||
							prevSettings.svgSettings.displayContainerMode ||
							"popover",
						buttonLabel:
							data?.svgSettings?.buttonLabel ||
							prevSettings.svgSettings.buttonLabel ||
							"Accessibility",
						labelButtonActive:
							data?.svgSettings?.labelButtonActive ??
							prevSettings.svgSettings.labelButtonActive ??
							false,
						showAccessibilityProfile:
							data?.svgSettings?.showAccessibilityProfile ??
							prevSettings.svgSettings.showAccessibilityProfile ??
							true,
					},
					features: enrichFeaturesForWidget(data?.features),
					statementSettings,
				}));
			} catch (e) {
				console.error("Failed to fetch frontend settings", e);
			} finally {
				setIsLoading(false);
			}
		}

		fetchSettings();
	}, []);

	// Helper to check if statement is enabled in widget
	const isStatementEnabledInWidget = () => {
		return (
			settings.statementSettings?.widget_enabled &&
			settings.statementSettings?.page_url
		);
	};

	// Get statement link data for widget
	const getStatementLinkData = () => {
		if (!isStatementEnabledInWidget()) return null;

		return {
			url: settings.statementSettings.page_url,
			text: settings.statementSettings.link_text || "Accessibility Statement",
			enabled: settings.statementSettings.widget_enabled,
		};
	};

	return {
		...settings,
		isLoading,
		isStatementEnabledInWidget,
		getStatementLinkData,
	};
}
